import React, { useState } from 'react';
import { Button, Typography } from 'antd';
import type {
  AssistantFeedback,
  AssistantFeedbackReason,
  ChatMessage,
  AssistantPublicAction,
} from '@app-types/assistant.ts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './AssistantWidget.css';

dayjs.extend(relativeTime);

const { Paragraph } = Typography;

export interface MessageBubbleProps {
  message: ChatMessage;
  onEndAndStartNew?: () => void;
  onFeedback?: (messageId: number, feedback: AssistantFeedback) => Promise<void>;
  onAction?: (messageId: number, action: AssistantPublicAction) => Promise<void>;
}

const NEGATIVE_REASONS: Array<{
  code: AssistantFeedbackReason;
  label: string;
}> = [
  { code: 'incorrect', label: 'Es incorrecta' },
  { code: 'incomplete', label: 'Está incompleta' },
  { code: 'unclear', label: 'No es clara' },
  { code: 'not_relevant', label: 'No es relevante' },
  { code: 'technical_problem', label: 'Hubo un problema técnico' },
];

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onEndAndStartNew,
  onFeedback,
  onAction,
}) => {
  const [showReasons, setShowReasons] = useState(
    message.feedback?.rating === 'not_helpful'
  );
  const [savingFeedback, setSavingFeedback] = useState(false);
  const isUser = message.role === 'user';
  const rootClassName = [
    'assistant-message',
    isUser ? 'assistant-message--user' : 'assistant-message--assistant',
  ].join(' ');

  return (
    <div className={rootClassName}>
      <div className="assistant-message__meta">
        <span>{isUser ? 'Tú' : 'Asistente'}</span>
        <span>{dayjs(message.created_at).fromNow()}</span>
      </div>
      <div className="assistant-message__bubble">
        <Paragraph className="assistant-message__content">
          {message.content}
        </Paragraph>
      </div>
      {!isUser && onFeedback ? (
        <div className="assistant-message__feedback" aria-label="Valorar respuesta">
          <div className="assistant-message__feedback-actions">
            <Button
              type={message.feedback?.rating === 'helpful' ? 'primary' : 'text'}
              size="small"
              loading={savingFeedback && !showReasons}
              disabled={savingFeedback}
              aria-pressed={message.feedback?.rating === 'helpful'}
              onClick={() => {
                setShowReasons(false);
                setSavingFeedback(true);
                void onFeedback(message.id, { rating: 'helpful' })
                  .catch(() => undefined)
                  .finally(() => setSavingFeedback(false));
              }}
            >
              Me sirvió
            </Button>
            <Button
              type={
                message.feedback?.rating === 'not_helpful' ? 'primary' : 'text'
              }
              size="small"
              loading={savingFeedback && showReasons}
              disabled={savingFeedback}
              aria-pressed={message.feedback?.rating === 'not_helpful'}
              onClick={() => {
                setShowReasons(true);
                setSavingFeedback(true);
                void onFeedback(message.id, { rating: 'not_helpful' })
                  .catch(() => undefined)
                  .finally(() => setSavingFeedback(false));
              }}
            >
              No me sirvió
            </Button>
          </div>
          {showReasons ? (
            <div className="assistant-message__feedback-reasons">
              <span>¿Qué faltó? (opcional)</span>
              {NEGATIVE_REASONS.map((reason) => (
                <Button
                  key={reason.code}
                  type={
                    message.feedback?.reason_code === reason.code
                      ? 'primary'
                      : 'text'
                  }
                  size="small"
                  disabled={savingFeedback}
                  aria-pressed={message.feedback?.reason_code === reason.code}
                  onClick={() => {
                    setSavingFeedback(true);
                    void onFeedback(message.id, {
                      rating: 'not_helpful',
                      reason_code: reason.code,
                    })
                      .catch(() => undefined)
                      .finally(() => setSavingFeedback(false));
                  }}
                >
                  {reason.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {!isUser && onAction && message.actions?.length ? (
        <div className="assistant-message__actions" aria-label="Acciones disponibles">
          {message.actions.map((action) => (
            <Button
              key={action.id}
              size="small"
              onClick={() => {
                void onAction(message.id, action).catch(() => undefined);
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}
      {!isUser && onEndAndStartNew ? (
        <Button
          type="link"
          size="small"
          onClick={onEndAndStartNew}
          className="assistant-message__restart"
        >
          Terminar y empezar nuevo chat
        </Button>
      ) : null}
    </div>
  );
};
